import { lazy } from "react";
import { createBrowserRouter } from "react-router";

import { AppShell } from "../layouts/AppShell";
import { AuthLayout } from "../layouts/AuthLayout";
import { RequireAuth } from "../features/auth/RequireAuth";
import { RedirectIfAuthenticated } from "../features/auth/RedirectIfAuthenticated";
import { RouteError } from "../components/RouteError";
import { LazyRoute as Lazy } from "../components/LazyRoute";

// Route modules are lazy so the sign-in screen is not paying for the data grid.
//
// The auth pages are the exception: they are the app's entry point, so
// code-splitting them means the first thing a visitor sees costs an extra
// round trip and renders a loading state over itself. They are ~2.6 kB each,
// and the vendor code they need (react-hook-form, zod) is already on the
// critical path because the shared schemas package puts zod in the entry
// graph. Splitting them bought nothing and cost a visible flash.
import SignInPage from "../features/auth/pages/SignInPage";
import ForgotPasswordPage from "../features/auth/pages/ForgotPasswordPage";
import SetPasswordPage from "../features/auth/pages/SetPasswordPage";

const HomePage = lazy(() => import("../features/home/HomePage"));
const UsersListPage = lazy(
  () => import("../features/users/pages/UsersListPage"),
);
const PatientsListPage = lazy(
  () => import("../features/patients/pages/PatientsListPage"),
);
const StudiesListPage = lazy(
  () => import("../features/studies/pages/StudiesListPage"),
);
const SurveysListPage = lazy(
  () => import("../features/surveys/pages/SurveysListPage"),
);
const HospitalSettingsPage = lazy(
  () => import("../features/settings/pages/HospitalSettingsPage"),
);
const AccountPage = lazy(
  () => import("../features/settings/pages/AccountPage"),
);
const AccessLogPage = lazy(
  () => import("../features/settings/pages/AccessLogPage"),
);
const AuditLogPage = lazy(
  () => import("../features/settings/pages/AuditLogPage"),
);
const SurveyDetailPage = lazy(
  () => import("../features/surveys/pages/SurveyDetailPage"),
);
const StudyFormPage = lazy(
  () => import("../features/studies/pages/StudyFormPage"),
);
const StudyDetailPage = lazy(
  () => import("../features/studies/pages/StudyDetailPage"),
);
const ScheduleSurveyPage = lazy(
  () => import("../features/studies/pages/ScheduleSurveyPage"),
);
const PatientFormPage = lazy(
  () => import("../features/patients/pages/PatientFormPage"),
);
const PatientDetailPage = lazy(
  () => import("../features/patients/pages/PatientDetailPage"),
);
const PatientSurveyFormPage = lazy(
  () => import("../features/patients/pages/PatientSurveyFormPage"),
);
const SurveyAnswersPage = lazy(
  () => import("../features/patients/pages/SurveyAnswersPage"),
);
const PatientMonitoringAnalysisPage = lazy(
  () => import("../features/analysis/pages/PatientMonitoringAnalysisPage"),
);
const PatientStudyAnalysisPage = lazy(
  () => import("../features/analysis/pages/PatientStudyAnalysisPage"),
);
const StudyAnalysisPage = lazy(
  () => import("../features/analysis/pages/StudyAnalysisPage"),
);
const UserFormPage = lazy(() => import("../features/users/pages/UserFormPage"));
const UserDetailPage = lazy(
  () => import("../features/users/pages/UserDetailPage"),
);
const NotFoundPage = lazy(() => import("../features/NotFoundPage"));

export const router = createBrowserRouter([
  {
    element: <RedirectIfAuthenticated />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: "/signin",
            element: <SignInPage />,
          },
          {
            path: "/forgot-password",
            element: <ForgotPasswordPage />,
          },
        ],
      },
    ],
  },

  // /activate and /reset-password are reachable while signed in — a clinician
  // may click a reset link from their inbox in an authenticated tab — so they
  // sit outside RedirectIfAuthenticated.
  {
    element: <AuthLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/activate",
        element: <SetPasswordPage mode="activate" />,
      },
      {
        path: "/reset-password",
        element: <SetPasswordPage mode="reset" />,
      },
    ],
  },

  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <HomePage />
              </Lazy>
            ),
          },
          {
            path: "/patients",
            element: (
              <Lazy>
                <PatientsListPage />
              </Lazy>
            ),
          },
          {
            path: "/patients/new",
            element: (
              <Lazy>
                <PatientFormPage mode="create" />
              </Lazy>
            ),
          },
          {
            path: "/patients/:patientId",
            element: (
              <Lazy>
                <PatientDetailPage />
              </Lazy>
            ),
          },
          {
            path: "/patients/:patientId/surveys/:surveyId/analysis",
            element: (
              <Lazy>
                <PatientMonitoringAnalysisPage />
              </Lazy>
            ),
          },
          // The answers themselves, addressed exactly as the API addresses
          // them: (patient, schedule, occurrence), naming no study, because a
          // schedule need not belong to one. One page serves both tabs.
          {
            path: "/patients/:patientId/surveys/:scheduledSurveyId/responses/:occurrenceKey",
            element: (
              <Lazy>
                <SurveyAnswersPage />
              </Lazy>
            ),
          },
          {
            path: "/patients/:patientId/surveys/new",
            element: (
              <Lazy>
                <PatientSurveyFormPage />
              </Lazy>
            ),
          },
          {
            path: "/patients/:patientId/edit",
            element: (
              <Lazy>
                <PatientFormPage mode="edit" />
              </Lazy>
            ),
          },
          // Keyed by the ENROLMENT, like the endpoint behind it: a score is a
          // statement about a patient inside one protocol, and the same patient
          // in two studies has two unrelated sets of answers.
          {
            path: "/patients/:patientId/studies/:patientStudyId/surveys/:surveyId/analysis",
            element: (
              <Lazy>
                <PatientStudyAnalysisPage />
              </Lazy>
            ),
          },
          {
            path: "/studies",
            element: (
              <Lazy>
                <StudiesListPage />
              </Lazy>
            ),
          },
          {
            path: "/studies/new",
            element: (
              <Lazy>
                <StudyFormPage mode="create" />
              </Lazy>
            ),
          },
          {
            path: "/studies/:studyId",
            element: (
              <Lazy>
                <StudyDetailPage />
              </Lazy>
            ),
          },
          // The cohort's scores for ONE questionnaire. Named in the URL for the
          // same reason the per-patient page names one: switching costs a
          // scoring pass over every patient who has answered.
          {
            path: "/studies/:studyId/analysis/:surveyId",
            element: (
              <Lazy>
                <StudyAnalysisPage />
              </Lazy>
            ),
          },
          {
            path: "/studies/:studyId/edit",
            element: (
              <Lazy>
                <StudyFormPage mode="edit" />
              </Lazy>
            ),
          },
          {
            path: "/studies/:studyId/surveys/new",
            element: (
              <Lazy>
                <ScheduleSurveyPage mode="create" />
              </Lazy>
            ),
          },
          {
            path: "/studies/:studyId/surveys/:scheduledSurveyId",
            element: (
              <Lazy>
                <ScheduleSurveyPage mode="edit" />
              </Lazy>
            ),
          },
          {
            path: "/surveys",
            element: (
              <Lazy>
                <SurveysListPage />
              </Lazy>
            ),
          },
          {
            path: "/surveys/:surveyId",
            element: (
              <Lazy>
                <SurveyDetailPage />
              </Lazy>
            ),
          },
          {
            path: "/users",
            element: (
              <Lazy>
                <UsersListPage />
              </Lazy>
            ),
          },
          {
            path: "/users/new",
            element: (
              <Lazy>
                <UserFormPage mode="create" />
              </Lazy>
            ),
          },
          {
            path: "/users/:userId",
            element: (
              <Lazy>
                <UserDetailPage />
              </Lazy>
            ),
          },
          {
            path: "/users/:userId/edit",
            element: (
              <Lazy>
                <UserFormPage mode="edit" />
              </Lazy>
            ),
          },
          {
            path: "/settings/hospital",
            element: (
              <Lazy>
                <HospitalSettingsPage />
              </Lazy>
            ),
          },
          {
            path: "/settings/audit",
            element: (
              <Lazy>
                <AuditLogPage />
              </Lazy>
            ),
          },
          {
            path: "/settings/access-log",
            element: (
              <Lazy>
                <AccessLogPage />
              </Lazy>
            ),
          },
          {
            path: "/settings/account",
            element: (
              <Lazy>
                <AccountPage />
              </Lazy>
            ),
          },
          {
            path: "*",
            element: (
              <Lazy>
                <NotFoundPage />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
]);
